import React from 'react';
import {
  UserGroupIcon,
  XMarkIcon,
  ClockIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  UserIcon
} from '@heroicons/react/24/outline';

export default function LoginsHelp() {
  return (
    <div className="max-w-4xl mx-auto p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-lg shadow-lg">
      <div className="text-center mb-8">
        <UserGroupIcon className="mx-auto h-16 w-16 text-blue-600 mb-4" />
        <h1 className="text-3xl font-bold text-gray-900 mb-2">User Sessions Management</h1>
        <p className="text-lg text-gray-600">Monitor active user sessions and manage system access</p>
      </div>

      {/* Overview Section */}
      <div className="bg-white rounded-lg p-6 mb-6 shadow-md">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
          <ShieldCheckIcon className="h-6 w-6 text-blue-600 mr-2" />
          Overview
        </h2>
        <p className="text-gray-700 mb-4">
          The User Sessions Management interface provides administrators with real-time visibility into all active user sessions across the platform.
          Monitor user activity, session durations, and take administrative actions when necessary to maintain system security and performance.
        </p>
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
          <p className="text-sm text-blue-700">
            <strong>Key Features:</strong> Real-time session monitoring, user boot functionality, session time tracking, and access level management.
          </p>
        </div>
      </div>

      {/* Interface Layout */}
      <div className="bg-white rounded-lg p-6 mb-6 shadow-md">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Interface Overview</h2>

        {/* Header Section */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-3">Header Controls</h3>
          <div className="bg-gray-100 rounded-lg p-4 border-2 border-dashed border-gray-300">
            <div className="flex justify-between items-center">
              <div>
                <h4 className="font-semibold text-gray-900">Users</h4>
                <p className="text-sm text-gray-600">A list of all the users in your account including their name, title, email and role.</p>
              </div>
              <button className="inline-flex items-center gap-x-2 rounded-md bg-red-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm opacity-75 cursor-not-allowed">
                <XMarkIcon aria-hidden="true" className="-ml-0.5 size-5" />
                Boot All Users
              </button>
            </div>
          </div>
        </div>

        {/* Table Structure */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-3">User Sessions Table</h3>
          <div className="bg-gray-100 rounded-lg p-4 border-2 border-dashed border-gray-300">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr>
                    <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900">Name</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Role</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Times</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Access</th>
                    <th className="relative py-3.5 pl-3 pr-4">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="whitespace-nowrap py-5 pl-4 pr-3 text-sm">
                      <div className="flex items-center">
                        <div className="size-11 shrink-0 bg-gray-200 rounded-full flex items-center justify-center">
                          <UserIcon className="h-6 w-6 text-gray-400" />
                        </div>
                        <div className="ml-4">
                          <div className="font-medium text-gray-900">John Doe</div>
                          <div className="text-gray-500">john.doe@example.com</div>
                        </div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">
                      <div className="text-gray-900">Team Lead</div>
                      <div className="text-gray-500">Development</div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">
                      <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                        Active
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">
                      <div className="text-gray-900">14:30 15/10/2025</div>
                      <div className="text-gray-500">16:30 15/10/2025</div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">user</td>
                    <td className="relative whitespace-nowrap py-5 pl-3 pr-4 text-right text-sm font-medium">
                      <button className="inline-flex items-center gap-x-2 rounded-md bg-red-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm opacity-75 cursor-not-allowed">
                        <XMarkIcon aria-hidden="true" className="-ml-0.5 size-5" />
                        Boot User
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Key Features */}
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg p-6 shadow-md">
          <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <ClockIcon className="h-5 w-5 text-blue-600 mr-2" />
            Session Monitoring
          </h3>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start">
              <span className="text-blue-500 mr-2">•</span>
              Real-time display of active user sessions
            </li>
            <li className="flex items-start">
              <span className="text-blue-500 mr-2">•</span>
              Session start and expiration times
            </li>
            <li className="flex items-start">
              <span className="text-blue-500 mr-2">•</span>
              User profile pictures and contact information
            </li>
            <li className="flex items-start">
              <span className="text-blue-500 mr-2">•</span>
              Department and role assignments
            </li>
          </ul>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-md">
          <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 text-orange-600 mr-2" />
            Administrative Actions
          </h3>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start">
              <span className="text-orange-500 mr-2">•</span>
              Boot individual users from the system
            </li>
            <li className="flex items-start">
              <span className="text-orange-500 mr-2">•</span>
              Bulk boot all users (admin only)
            </li>
            <li className="flex items-start">
              <span className="text-orange-500 mr-2">•</span>
              Prevent admin account termination
            </li>
            <li className="flex items-start">
              <span className="text-orange-500 mr-2">•</span>
              Immediate session termination
            </li>
          </ul>
        </div>
      </div>

      {/* Usage Guidelines */}
      <div className="bg-white rounded-lg p-6 shadow-md">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Usage Guidelines</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">When to Use</h3>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                Monitor suspicious user activity
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                Manage system performance during peak usage
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                Handle security incidents
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                Perform maintenance operations
              </li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">Best Practices</h3>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <span className="text-blue-500 mr-2">•</span>
                Regularly review active sessions
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 mr-2">•</span>
                Document reasons for user termination
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 mr-2">•</span>
                Communicate with affected users
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 mr-2">•</span>
                Monitor for unusual access patterns
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}